import React, { useState, useRef } from 'react';
import { Download, FileSpreadsheet, CheckCircle, AlertTriangle, XCircle, ChevronLeft, Layers } from 'lucide-react';
import api from '../utils/api';
import { downloadExport } from '../utils/exportUtils';
import { fmtCurrency } from '../utils/helpers';
import { Modal, FormField } from './Table';
import ClientSearchDropdown from './ClientSearchDropdown';
import toast from 'react-hot-toast';

// Payment update sheet: export outstanding invoices -> fill five columns in
// Excel -> import back. The importer only ever reads the entry columns, so
// edits to the reference columns cannot change an invoice.

const ddmmyyyy = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

export function PaymentExportModal({ onClose }) {
  const [scope, setScope] = useState('all');           // all | client | dates
  const [clientId, setClientId] = useState(null);
  const [clientName, setClientName] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const go = () => {
    const params = {};
    if (scope === 'client') {
      if (!clientId) { toast.error('Pick a client first'); return; }
      params.client_id = clientId;
    }
    if (scope === 'dates') {
      if (!from && !to) { toast.error('Pick at least one date'); return; }
      if (from) params.from_date = from;
      if (to) params.to_date = to;
    }
    downloadExport('/billing/payments/export', params, `Payment_Update_${ddmmyyyy()}.xlsx`);
    onClose();
  };

  const Radio = ({ v, label, hint }) => (
    <label className="flex items-start gap-2 cursor-pointer py-1">
      <input type="radio" checked={scope === v} onChange={() => setScope(v)} className="mt-1" />
      <span>
        <span className="text-sm text-gray-800">{label}</span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
    </label>
  );

  return (
    <Modal title="Export payment update sheet" onClose={onClose} size="md">
      <div className="space-y-3">
        <p className="text-xs text-gray-500">
          One row per invoice that still has a balance. Fill in the last five columns in Excel, then import the file back.
        </p>
        <Radio v="all" label="All outstanding invoices" />
        <Radio v="client" label="One client only" />
        {scope === 'client' && (
          <div className="pl-6">
            <ClientSearchDropdown
              value={clientId || ''}
              initialLabel={clientName}
              onChange={(id, name) => { setClientId(id); setClientName(name); }}
            />
          </div>
        )}
        <Radio v="dates" label="Invoice date range" />
        {scope === 'dates' && (
          <div className="pl-6 flex items-center gap-2">
            <FormField label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} className="input-field" /></FormField>
            <FormField label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} className="input-field" /></FormField>
          </div>
        )}
        <div className="flex gap-3 pt-3 border-t border-gray-100">
          <button onClick={go} className="btn-primary flex-1 flex items-center justify-center gap-2"><Download size={15} /> Download sheet</button>
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

const Dot = ({ row }) => {
  if (row.action === 'skip') return <XCircle size={14} className="text-red-500 flex-shrink-0" />;
  if (row.overpayment || row.warning) return <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />;
  return <CheckCircle size={14} className="text-green-500 flex-shrink-0" />;
};

export function PaymentImportModal({ onImported, onClose }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [acceptOver, setAcceptOver] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  const handleFile = async f => {
    if (!f) return;
    setFile(f); setLoading(true); setAcceptOver(false);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/billing/payments/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data); setStep(2);
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not read that file'); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (acceptOver) fd.append('accept_overpayments', 'true');
      const r = await api.post('/billing/payments/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data); setStep(3);
      onImported?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.error || 'Import failed — nothing was saved'); }
    finally { setLoading(false); }
  };

  const blockedByOverpayment = preview?.overpayments > 0 && !acceptOver;

  return (
    <Modal title="Import payment sheet" onClose={onClose} size="lg">
      {step === 1 && (
        <div className="space-y-4">
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl py-10 text-center cursor-pointer transition-colors ${dragging ? 'border-[#1e3a5f] bg-blue-50/40' : 'border-gray-200 hover:border-gray-300'}`}
          >
            <FileSpreadsheet size={30} className="mx-auto mb-2 text-gray-300" />
            <div className="text-sm font-medium text-gray-700">{loading ? 'Reading file…' : 'Drop the filled payment sheet here, or click to choose'}</div>
            <div className="text-xs text-gray-400 mt-1">Rows with a blank Amount Received are skipped. Up to 500 rows.</div>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={e => handleFile(e.target.files[0])} />
          </div>
          <p className="text-xs text-gray-400">
            Only the last five columns are read. Anything you changed in the reference columns is ignored — an import can never alter an invoice.
          </p>
        </div>
      )}

      {step === 2 && preview && (
        <div className="space-y-4">
          <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
            <div className="text-sm font-semibold text-gray-800">
              {preview.toAdd} receipt{preview.toAdd !== 1 ? 's' : ''} will be added, totalling {fmtCurrency(preview.totalAmount)}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {preview.toSkip} skipped{preview.blank > 0 ? ` · ${preview.blank} blank row${preview.blank !== 1 ? 's' : ''} ignored` : ''}
            </div>
          </div>

          <div className="border border-gray-100 rounded-xl max-h-64 overflow-y-auto divide-y divide-gray-50">
            {preview.rows.map((r, i) => (
              <div key={i} className="flex items-start gap-2 px-3 py-2 text-xs">
                <Dot row={r} />
                <span className="text-gray-400 w-12 flex-shrink-0">Row {r.rowNum}</span>
                <span className="font-mono font-medium text-gray-800 w-32 flex-shrink-0 truncate">
                  {r.invoice_no}
                  {r.multi && <Layers size={10} className="inline ml-1 text-blue-400" title="Part payment — more than one row for this invoice" />}
                </span>
                <span className="text-gray-500 flex-1 min-w-0 truncate">{r.client_name || ''}</span>
                <span className={`flex-shrink-0 text-right max-w-[45%] ${r.action === 'skip' ? 'text-red-600' : r.overpayment ? 'text-amber-700' : 'text-gray-500'}`}>
                  {r.action === 'skip' ? r.reason
                    : r.overpayment ? `More than the ${fmtCurrency(Math.max(r.balance_before, 0))} balance`
                    : r.warning || 'Will be added'}
                </span>
              </div>
            ))}
          </div>

          {preview.overpayments > 0 && (
            <label className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
              <input type="checkbox" checked={acceptOver} onChange={e => setAcceptOver(e.target.checked)} className="mt-0.5" />
              <span className="text-amber-800">
                {preview.overpayments} row{preview.overpayments !== 1 ? 's are' : ' is'} more than the balance due. Tick to accept the overpayment{preview.overpayments !== 1 ? 's' : ''}.
              </span>
            </label>
          )}

          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <button onClick={() => { setStep(1); setPreview(null); setFile(null); }} className="btn-secondary flex-1 flex items-center justify-center gap-1.5">
              <ChevronLeft size={14} /> Choose another file
            </button>
            <button onClick={confirm} disabled={loading || preview.toAdd === 0 || blockedByOverpayment} className="btn-primary flex-1 disabled:opacity-40">
              {loading ? 'Importing…' : 'Confirm import'}
            </button>
          </div>
          <p className="text-xs text-gray-400">Nothing is saved until you click Confirm import.</p>
        </div>
      )}

      {step === 3 && result && (
        <div className="space-y-4 text-center py-4">
          <CheckCircle size={36} className="mx-auto text-green-500" />
          <div className="text-sm font-medium text-gray-800">
            {result.added} added, {result.skipped} skipped — {fmtCurrency(result.totalAmount)} recorded
          </div>
          {result.warnings?.length > 0 && (
            <div className="text-left text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-700 space-y-1 max-h-32 overflow-y-auto">
              {result.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
          <button onClick={onClose} className="btn-primary px-8">Done</button>
        </div>
      )}
    </Modal>
  );
}
